import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { SITE_CONTAINER } from "@/lib/siteLayout";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { formatPrice, formatKm } from "@/lib/format";
import { Plus, Pencil, Trash2, Loader2, LogOut, User, MessageSquare, Eye, Users, Clock3, AlertOctagon, AlertTriangle, Gift, Settings2, Lock } from "lucide-react";
import { SellerProfileSection } from "@/components/SellerProfileSection";
import { isListingPendingApproval, sortSellerDashboardListings } from "@/lib/sellerListingOrder";
import { ListingDefaultsDialog } from "@/components/ListingDefaultsDialog";
import { ChatConversationList } from "@/components/ChatConversationList";
import { toast } from "sonner";
import api from "@/lib/api";
import { useChat } from "@/context/ChatContext";
import { StatsChart } from "@/components/StatsChart";
import { statsColorForCar, statsEnabledCars } from "@/lib/statsChartColors";
import { CreditHistory } from "@/components/CreditHistory";
import { CreditForecast } from "@/components/CreditForecast";
import { AddonPanel } from "@/components/AddonPanel";
import { GiftCreditsDialog } from "@/components/GiftCreditsDialog";
import { SellerAssistantHub } from "@/components/SellerAssistantHub";
import { SellerAssistantLocked } from "@/components/SellerAssistantLocked";

export default function Dashboard() {
  const { user, logout, updateProfile, refreshUser } = useAuth();
  const navigate = useNavigate();
  const chat = useChat();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [config, setConfig] = useState(null);
  const [refreshTs, setRefreshTs] = useState(0);
  const [statsChartCarId, setStatsChartCarId] = useState("all");
  const [giftOpen, setGiftOpen] = useState(false);
  const [listingDefaultsOpen, setListingDefaultsOpen] = useState(false);
  const profileInit = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkDone = useRef(false);

  useEffect(() => {
    const cid = searchParams.get("conversation");
    if (!cid || deepLinkDone.current || chat.convs.length === 0) return;
    const conv = chat.convs.find((c) => c.id === cid);
    if (conv) {
      deepLinkDone.current = true;
      chat.openChatConv(conv);
      setSearchParams({}, { replace: true });
    }
  }, [chat.convs, searchParams]);

  useEffect(() => {
    if (user && !profileInit.current) {
      setProfile({ name: user.name || "", phone: user.phone || "" });
      profileInit.current = true;
    }
  }, [user]);

  const [stats, setStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/my/cars");
      setCars(res.data);
    } finally {
      setLoading(false);
    }
    api.get("/my/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    refreshUser().catch(() => {});
    api.get("/pricing-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get("listingDefaults") === "1") {
      setListingDefaultsOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const removeCar = async (car) => {
    if (!window.confirm(`Törlöd: ${car.make} ${car.model}?`)) return;
    try {
      await api.delete(`/my/cars/${car.id}`);
      toast.success("Hirdetés törölve");
      load();
    } catch (e) {
      toast.error("Törlés sikertelen");
    }
  };

  const onAddonChange = (updated) => {
    setCars((cs) => cs.map((c) => c.id === updated.id ? updated : c));
    refreshUser().catch(() => {});
    setRefreshTs(Date.now());
  };

  const statsCarIds = useMemo(() => statsEnabledCars(cars).map((c) => c.id), [cars]);
  const sortedCars = useMemo(() => sortSellerDashboardListings(cars), [cars]);
  const pendingCount = useMemo(() => cars.filter(isListingPendingApproval).length, [cars]);
  const hasListingAiSupport = useMemo(
    () => cars.some((c) => c.addon_ai_customer_support),
    [cars]
  );

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white">
      <Navbar />
      <section className="pt-28 pb-20">
        <div className={SITE_CONTAINER}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-electric">Eladói fiók</div>
              <h1 className="font-heading text-3xl md:text-4xl font-light mt-1">Üdv, {user?.name || "Eladó"}!</h1>
            </div>
            <button
              data-testid="dashboard-logout"
              onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
            >
              <LogOut className="w-4 h-4" /> Kilépés
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
            {/* Profile */}
            <div data-testid="profile-card" className="glass rounded-3xl p-6">
              <div className="flex items-center gap-2 text-sm text-slate-300 mb-4">
                <User className="w-4 h-4 text-electric" strokeWidth={1.5} /> Profiladatok
              </div>
              <div className="mb-4">
                <label className="text-xs uppercase tracking-wide text-slate-400 block mb-2">Email</label>
                <div className="text-sm text-slate-400 p-3 bg-white/[0.02] rounded-xl border border-white/5">{user?.email}</div>
              </div>
              <SellerProfileSection
                name={profile.name}
                phone={profile.phone}
                onNamePhoneChange={({ name, phone }) => setProfile({ name, phone })}
                onSaved={() => refreshUser().catch(() => {})}
              />
            </div>

            {/* Kreditek + eladói asszisztens */}
            <div className="lg:col-span-2 flex flex-col gap-4 min-h-[640px]">
              {(user?.credits ?? 0) < 20 && (
                <div data-testid="low-credit-warning" className="flex items-start gap-2.5 p-3.5 rounded-2xl bg-amber-500/10 border border-amber-400/30 text-sm shrink-0">
                  <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="flex-1">
                    <div className="text-amber-200 font-medium">Alacsony kredit egyenleg</div>
                    <div className="text-amber-100/80 text-xs mt-0.5">Már csak <b>{user?.credits ?? 0} kredited</b> maradt. A napi 10 krediten kifogyva a hirdetéseid automatikusan lekerülnek.</div>
                  </div>
                  <a href="/kreditek" data-testid="low-credit-topup" className="shrink-0 inline-flex items-center px-3 py-1.5 rounded-full bg-electric hover:bg-electric-light text-white text-xs">Kredit vásárlása</a>
                </div>
              )}
              <div className="shrink-0">
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    data-testid="open-gift-from-dashboard"
                    onClick={() => setGiftOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs border border-emerald-400/25"
                  >
                    <Gift className="w-3.5 h-3.5" /> Kredit ajándékozása
                  </button>
                </div>
                <CreditHistory balance={user?.credits ?? 0} onRefresh={refreshTs} />
                <div className="mt-4">
                  <CreditForecast key={refreshTs} />
                </div>
              </div>
              <div className="flex-1 flex flex-col min-h-[520px]">
                {hasListingAiSupport ? <SellerAssistantHub /> : <SellerAssistantLocked />}
              </div>
            </div>
          </div>

          {/* Inbox */}
          <div className="mt-12" data-testid="inbox-section">
            <div className="flex items-center gap-2 mb-6">
              <MessageSquare className="w-5 h-5 text-electric" strokeWidth={1.5} />
              <h2 className="font-heading text-2xl font-light">Üzenetek</h2>
            </div>
            {chat.convs.length === 0 ? (
              <div className="glass rounded-3xl p-8 text-center text-slate-400">
                Még nincs beszélgetésed. Amint egy érdeklődő ír egy autódra, itt jelenik meg.
              </div>
            ) : (
              <div className="glass rounded-2xl overflow-hidden">
                <ChatConversationList
                  convs={chat.convs}
                  onSelect={(c) => chat.openChatConv(c)}
                  testidPrefix="conversation"
                  emptyHint="Még nincs beszélgetésed. Amint egy érdeklődő ír egy autódra, itt jelenik meg."
                />
              </div>
            )}
          </div>

          {/* Listings */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-12 mb-6">
            <h2 className="font-heading text-2xl font-light">Aktív hirdetéseim</h2>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="listing-defaults-btn"
                onClick={() => setListingDefaultsOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass hover:bg-white/[0.08] border border-white/10 text-sm text-slate-200 transition-all"
              >
                <Settings2 className="w-4 h-4 text-electric" strokeWidth={1.75} />
                Hirdetés beállítások
              </button>
              <button
                data-testid="new-listing-btn"
                onClick={() => navigate("/fiok/uj-hirdetes")}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium transition-all"
              >
                <Plus className="w-4 h-4" /> Új hirdetés
              </button>
            </div>
          </div>

          <ListingDefaultsDialog
            open={listingDefaultsOpen}
            onClose={() => setListingDefaultsOpen(false)}
            user={user}
            pricingConfig={config}
            onSave={async (default_listing_addons) => {
              await updateProfile({ default_listing_addons });
            }}
          />

          {stats && cars.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mb-6 w-full" data-testid="seller-stats">
              <div className="glass rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-electric/15 flex items-center justify-center shrink-0">
                  <Eye className="w-4 h-4 text-electric" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading text-2xl font-light" data-testid="stats-total-views">{stats.total_views}</div>
                  <div className="text-xs text-slate-400">Összes megtekintés</div>
                </div>
              </div>
              <div className="glass rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading text-2xl font-light" data-testid="stats-total-interest">{stats.total_interest}</div>
                  <div className="text-xs text-slate-400">Érdeklődő</div>
                </div>
              </div>
            </div>
          )}

          {cars.length > 0 && (
            <StatsChart cars={cars} carId={statsChartCarId} onCarIdChange={setStatsChartCarId} />
          )}

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-electric animate-spin" /></div>
          ) : cars.length === 0 ? (
            <div data-testid="no-listings" className="glass rounded-3xl p-12 text-center text-slate-400">
              Még nincs feladott hirdetésed. Kattints az „Új hirdetés" gombra az első autó feltöltéséhez.
            </div>
          ) : (
            <>
            {pendingCount > 0 && (
              <p
                data-testid="seller-pending-listings-hint"
                className="text-xs text-amber-200/90 mb-4 px-1 leading-relaxed"
              >
                {pendingCount} hirdetés jóváhagyásra vár — a lista alján, elsötétítve jelennek meg. Szerkeszthetők, a kiegészítők jóváhagyás után aktiválhatók.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-3">
              {sortedCars.map((car) => {
                const isExpired = car.status === "expired";
                const isPending = isListingPendingApproval(car);
                const statsColor = car.detailed_stats ? statsColorForCar(car.id, statsCarIds) : null;
                const statsChartActive = car.detailed_stats && statsChartCarId === car.id;
                return (
                <div
                  key={car.id}
                  data-testid={`listing-${car.id}`}
                  data-stats-active={car.detailed_stats ? "true" : undefined}
                  data-listing-pending={isPending ? "true" : undefined}
                  className={`glass rounded-2xl overflow-hidden transition-all min-w-0 ${
                    isPending ? "opacity-[0.82] saturate-[0.85]" : ""
                  } ${
                    statsChartActive ? "ring-2" : statsColor ? "ring-1 ring-white/10" : ""
                  }`}
                  style={
                    statsColor
                      ? {
                          borderLeft: `4px solid ${statsColor}`,
                          ...(statsChartActive ? { boxShadow: `0 0 0 2px ${statsColor}66` } : {}),
                        }
                      : undefined
                  }
                >
                  <div className="aspect-[16/9] overflow-hidden relative rounded-t-2xl bg-black/30">
                    <img
                      src={car.image}
                      alt={car.model}
                      className={`w-full h-full object-cover ${isPending ? "brightness-[0.55]" : ""}`}
                    />
                    {isPending && (
                      <div
                        data-testid={`listing-pending-lock-${car.id}`}
                        className="absolute inset-0 z-[6] flex flex-col items-center justify-center bg-black/35 pointer-events-none"
                      >
                        <div className="rounded-2xl bg-black/50 border border-amber-400/30 px-4 py-3 flex flex-col items-center gap-1.5">
                          <Lock className="w-7 h-7 text-amber-200" strokeWidth={1.75} />
                          <span className="text-[10px] font-semibold tracking-[0.12em] uppercase text-amber-100">
                            Jóváhagyás alatt
                          </span>
                        </div>
                      </div>
                    )}
                    {car.status === "public" ? (
                      <span
                        data-testid={`listing-status-live-${car.id}`}
                        className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/90 backdrop-blur-md text-white text-[10px] font-semibold tracking-[0.15em] uppercase shadow-lg shadow-emerald-500/25"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Élő hirdetés
                      </span>
                    ) : isExpired ? (
                      <span
                        data-testid={`listing-status-expired-${car.id}`}
                        className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/90 backdrop-blur-md text-white text-[10px] font-semibold tracking-[0.15em] uppercase shadow-lg"
                        title="A hirdetés lejárt vagy elfogyott a kredit"
                      >
                        <AlertOctagon className="w-2.5 h-2.5" strokeWidth={2.25} /> Lejárt
                      </span>
                    ) : (
                      <span
                        data-testid={`listing-status-pending-${car.id}`}
                        className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/90 backdrop-blur-md text-white text-[10px] font-semibold tracking-[0.15em] uppercase shadow-lg shadow-amber-500/25"
                      >
                        <Clock3 className="w-2.5 h-2.5" strokeWidth={2.25} /> Jóváhagyás alatt
                      </span>
                    )}
                  </div>
                  <div className="p-4 lg:p-3">
                    <div className="text-xs text-slate-400 truncate">{car.make} · {car.year}</div>
                    <div className="font-heading text-lg lg:text-base font-medium truncate">{car.model}</div>
                    <div className="text-electric font-medium mt-1">{formatPrice(car.price)}</div>
                    <div className="text-xs text-slate-400 mt-1">{car.horsepower} LE · {formatKm(car.mileage)}</div>
                    <div className="text-[11px] text-slate-500 mt-1" data-testid={`listing-lifetime-${car.id}`}>
                      Nap {car.days_alive || 0} / {car.max_days || 30}
                    </div>
                    {car.detailed_stats && statsColor && (
                      <button
                        type="button"
                        data-testid={`listing-stats-color-${car.id}`}
                        onClick={() => setStatsChartCarId(car.id)}
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-white transition-colors"
                      >
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: statsColor }} />
                        Részletes statisztika — megnyitás a grafikonon
                      </button>
                    )}
                    {stats?.per_car?.[car.id] && (
                      <div className="flex items-center gap-4 mt-2 text-xs" data-testid={`listing-stats-${car.id}`}>
                        <span className="inline-flex items-center gap-1 text-slate-300">
                          <Eye className="w-3.5 h-3.5 text-electric" strokeWidth={1.5} /> {stats.per_car[car.id].views} megt.
                        </span>
                        <span className="inline-flex items-center gap-1 text-slate-300">
                          <Users className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} /> {stats.per_car[car.id].interest} érd.
                        </span>
                      </div>
                    )}

                    {isPending ? (
                      <p
                        data-testid={`listing-pending-addons-locked-${car.id}`}
                        className="mt-3 pt-3 border-t border-white/5 text-[11px] text-amber-200/75 leading-relaxed"
                      >
                        Kiegészítők és nyilvános megjelenés admin jóváhagyás után érhetők el. Addig szerkesztheted a hirdetést.
                      </p>
                    ) : (
                      <AddonPanel
                        car={car}
                        config={config}
                        onChange={onAddonChange}
                        onEditListing={(opts = {}) =>
                          navigate("/fiok/uj-hirdetes", { state: { car, ...opts } })
                        }
                      />
                    )}

                    <div className="flex gap-2 mt-4">
                      <button
                        data-testid={`edit-listing-${car.id}`}
                        onClick={() => navigate("/fiok/uj-hirdetes", { state: { car } })}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg glass hover:bg-white/10 text-sm transition-all"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Szerkeszt
                      </button>
                      <button
                        data-testid={`delete-listing-${car.id}`}
                        onClick={() => removeCar(car)}
                        className="px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </section>
      <Footer />
      <GiftCreditsDialog
        open={giftOpen}
        onClose={() => setGiftOpen(false)}
        balance={user?.credits ?? 0}
        onSent={() => { refreshUser().catch(() => {}); setRefreshTs(Date.now()); }}
      />
    </div>
  );
}
